import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { haptic } from "../telegram";
import { useBackNavigation } from "../use-back-navigation";

type RestrictedUser = {
  userId: string;
  status: "frozen" | "banned";
  displayName: string | null;
  age: number | null;
  city: string | null;
  updatedAt: string;
  lastAction: null | {
    action: string;
    reason: string | null;
    createdAt: string | null;
  };
};

type RestrictedPage = {
  items: RestrictedUser[];
  nextCursor: string | null;
};

type PendingAction = {
  user: RestrictedUser;
  status: "active" | "banned";
};

export function AdminRestrictedUsers({ role, onChanged }: { role: string; onChanged(): Promise<void> }) {
  const [error, setError] = useState("");
  const [items, setItems] = useState<RestrictedUser[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const canChangeStatus = role === "admin" || role === "super_admin";

  const load = useCallback(async () => {
    const result = await api<RestrictedPage>("/v1/admin/moderation/restricted-users?limit=30");
    setItems(result.items);
    setNextCursor(result.nextCursor);
  }, []);

  useEffect(() => {
    void load().catch(() => {
      setItems([]);
      setError("Не удалось загрузить список аккаунтов.");
    });
  }, [load]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError("");
    try {
      const result = await api<RestrictedPage>(
        `/v1/admin/moderation/restricted-users?limit=30&cursor=${encodeURIComponent(nextCursor)}`,
      );
      setItems((current) => [...(current ?? []), ...result.items]);
      setNextCursor(result.nextCursor);
    } catch {
      setError("Не удалось загрузить следующую страницу.");
    } finally {
      setLoadingMore(false);
    }
  };

  const begin = (user: RestrictedUser, status: "active" | "banned") => {
    setPending({ user, status });
    setReason(status === "active" ? "manual_review_complete" : "confirmed_policy_violation");
    setNote("");
  };

  const apply = async () => {
    if (!pending || !reason.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await api(`/v1/admin/users/${pending.user.userId}/status`, {
        method: "POST",
        body: JSON.stringify({
          status: pending.status,
          reason: reason.trim(),
          ...(note.trim() ? { note: note.trim() } : {}),
        }),
      });
      haptic("success");
      setItems((items) => items?.filter((item) => item.userId !== pending.user.userId) ?? null);
      setPending(null);
      setReason("");
      setNote("");
      await Promise.all([load(), onChanged()]).catch(() => setError("Статус сохранён. Не удалось обновить список."));
    } catch {
      setError("Не удалось выполнить действие. Данные сохранены на экране; повторите попытку.");
    } finally {
      setBusy(false);
    }
  };

  useBackNavigation(() => {
    if (busy) return;
    setPending(null);
  }, Boolean(pending));
  const refresh = async () => {
    setError("");
    try {
      await load();
    } catch {
      setError("Не удалось обновить список аккаунтов.");
    }
  };
  const feedback = error && (
    <div className="reference-error" role="alert">
      {error}
      <button type="button" className="secondary-button" disabled={busy || loadingMore} onClick={() => void refresh()}>
        Повторить загрузку
      </button>
    </div>
  );
  if (error && !pending && !items?.length) return <section className="admin-load-error">{feedback}</section>;
  if (!items) return <div className="list-skeleton" />;

  const permissionNote = !canChangeStatus && (
    <section className="admin-permission-note">
      <strong>Только просмотр</strong>
      <p>Модератор видит ограничения. Восстановление и блокировка доступны администратору.</p>
    </section>
  );

  if (pending) {
    const displayName = pending.user.displayName ?? "Профиль без имени";
    const isRestore = pending.status === "active";
    return (
      <section className="restricted-confirmation">
        <button type="button" className="admin-back-button" disabled={busy} onClick={() => setPending(null)}>
          ← Назад
        </button>
        <div className="report-detail-heading">
          <div>
            <span className="eyebrow">{isRestore ? "Восстановление доступа" : "Блокировка аккаунта"}</span>
            <h2>{displayName}</h2>
            <p>
              Текущий статус: <strong>{pending.user.status}</strong>
            </p>
          </div>
        </div>

        <label className="moderator-note">
          <span>Причина решения</span>
          <input
            maxLength={120}
            value={reason}
            placeholder="Причина для audit trail"
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <label className="moderator-note">
          <span>
            Внутренняя заметка <em>необязательно</em>
          </span>
          <textarea
            maxLength={500}
            value={note}
            placeholder="Дополнительный контекст"
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        {feedback}
        <div className={isRestore ? "restore-confirmation" : "danger-confirmation"}>
          <strong>{isRestore ? "Вернуть доступ к аккаунту?" : "Заблокировать аккаунт постоянно?"}</strong>
          <p>
            {isRestore
              ? "Старые сессии останутся отозванными. Пользователю потребуется повторная Telegram-аутентификация."
              : "Активные сессии будут отозваны, а аккаунт перестанет участвовать в Discovery и Messaging."}
          </p>
          <div className="admin-actions">
            <button type="button" className="secondary-button" disabled={busy} onClick={() => setPending(null)}>
              Отмена
            </button>
            <button
              type="button"
              className={isRestore ? "approve-button" : "danger-button"}
              disabled={busy || !reason.trim()}
              onClick={apply}
            >
              {busy ? "Сохраняем…" : "Подтвердить"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="empty-state admin-empty">
        <div className="empty-orbit">✓</div>
        <h2>Ограничений нет</h2>
        <p>Здесь появятся замороженные и заблокированные аккаунты.</p>
        <button type="button" className="secondary-button" onClick={() => void refresh()}>
          Обновить
        </button>
      </section>
    );
  }

  return (
    <section className="restricted-list">
      {feedback}
      {permissionNote}
      {items.map((user) => (
        <article className="restricted-row" key={user.userId}>
          <div className="restricted-row-main">
            <div>
              <strong>
                {user.displayName ?? "Профиль без имени"}
                {user.age ? `, ${user.age}` : ""}
              </strong>
              <span>{user.city ?? "Город не указан"}</span>
            </div>
            <span className={`account-status ${user.status}`}>{user.status}</span>
          </div>
          <div className="restricted-meta">
            <span>{user.lastAction?.reason ?? "Причина не указана"}</span>
            <time>{new Date(user.updatedAt).toLocaleString("ru-RU")}</time>
          </div>
          {canChangeStatus && (
            <div className="restricted-actions">
              <button type="button" className="approve-button" onClick={() => begin(user, "active")}>
                Восстановить
              </button>
              {user.status === "frozen" && (
                <button type="button" className="danger-button" onClick={() => begin(user, "banned")}>
                  Заблокировать
                </button>
              )}
            </div>
          )}
        </article>
      ))}
      {nextCursor && (
        <button type="button" className="load-more-conversations" disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? "Загружаем…" : "Показать ещё"}
        </button>
      )}
    </section>
  );
}
